import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  onSnapshot,
  query,
  updateDoc,
} from "firebase/firestore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ScienceIcon from "@mui/icons-material/Science";
import { updateMeta } from "../utils/auditFields";
import PropertyContext from "../contexts/PropertyContext";
import { propertyDoc, propertyCollection } from "../utils/firestorePaths";

const STATUS_COLOR = {
  pending: "warning",
  approved: "success",
  rejected: "error",
};

const formatDate = (value) => {
  if (!value) return "";
  const date = value.seconds ? new Date(value.seconds * 1000) : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
};

const currency = (amount) =>
  `$${Number(amount || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const PartsRequestReview = ({ userInfo }) => {
  const { selectedProperty, selectedClient } = useContext(PropertyContext);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!selectedProperty) return undefined;
    setLoading(true);
    const partsRef = propertyCollection(
      selectedClient,
      selectedProperty,
      "partsRequests"
    );
    const unsubscribe = onSnapshot(query(partsRef), (snapshot) => {
      const rows = snapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((r) => Array.isArray(r.items))
        .sort((a, b) => {
          const at = a.requestedAt?.seconds || 0;
          const bt = b.requestedAt?.seconds || 0;
          return bt - at;
        });
      setRequests(rows);
      setLoading(false);
    });
    return unsubscribe;
  }, [selectedProperty]);

  const pending = useMemo(
    () => requests.filter((r) => (r.status || "pending") === "pending"),
    [requests]
  );
  const reviewed = useMemo(
    () => requests.filter((r) => (r.status || "pending") !== "pending"),
    [requests]
  );

  const setStatus = async (requestId, status) => {
    setBusyId(requestId);
    try {
      const requestRef = propertyDoc(
        selectedClient,
        selectedProperty,
        "partsRequests",
        requestId
      );
      await updateDoc(requestRef, {
        status,
        reviewedAt: new Date(),
        ...updateMeta(userInfo),
      });
    } catch (err) {
      console.error("Failed to update request status:", err);
    } finally {
      setBusyId(null);
    }
  };

  const renderRequest = (req) => {
    const itemCount =
      req.itemCount ??
      req.items.reduce((sum, i) => sum + Number(i.count || 0), 0);
    return (
      <Accordion key={req.id} disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            pr={1}
          >
            <Box>
              <Typography fontWeight={600}>
                {req.requestedByName || "Unknown"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatDate(req.requestedAt)} · {itemCount} item
                {itemCount !== 1 ? "s" : ""}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography fontWeight={600}>
                {currency(req.totalEstimated)}
              </Typography>
              <Chip
                size="small"
                label={req.status || "pending"}
                color={STATUS_COLOR[req.status] || "default"}
                sx={{ textTransform: "capitalize" }}
              />
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Item</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell align="right">Est. Cost</TableCell>
                <TableCell align="right">Line Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {req.items.map((item, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      {item.itemName}
                      {item.isChemical && (
                        <ScienceIcon fontSize="small" color="info" />
                      )}
                    </Box>
                    {item.itemSKU && (
                      <Typography variant="caption" color="text.secondary">
                        {item.itemSKU}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{item.count}</TableCell>
                  <TableCell align="right">{currency(item.itemCost)}</TableCell>
                  <TableCell align="right">
                    {currency(Number(item.itemCost || 0) * Number(item.count || 0))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {(req.status || "pending") === "pending" && (
            <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
              <Button
                color="error"
                variant="outlined"
                disabled={busyId === req.id}
                onClick={() => setStatus(req.id, "rejected")}
              >
                Reject
              </Button>
              <Button
                color="success"
                variant="contained"
                disabled={busyId === req.id}
                onClick={() => setStatus(req.id, "approved")}
              >
                Approve
              </Button>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  const visible = tab === 0 ? pending : reviewed;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 900, mx: "auto" }}>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Parts Request Approvals
      </Typography>
      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={`Pending (${pending.length})`} />
        <Tab label={`Reviewed (${reviewed.length})`} />
      </Tabs>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : visible.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 6, textAlign: "center" }}>
          {tab === 0 ? "No pending requests" : "No reviewed requests"}
        </Typography>
      ) : (
        <Stack spacing={1}>{visible.map(renderRequest)}</Stack>
      )}
    </Box>
  );
};

export default PartsRequestReview;
